if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();

        const isComplementary = this.classList.contains('complementary-products');

        if (isComplementary) {
          // Inline <script> may have already rendered from comp-html cache
          const alreadyRendered = this.innerHTML.trim().length > 0;
          if (alreadyRendered) {
            // Content visible — refresh from API in background (low priority)
            requestIdleCallback(() => this._initComplementary(), { timeout: 3000 });
          } else {
            // No cache — fetch API right away
            this._initComplementary();
          }
        } else {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(this.init.bind(this), { timeout: 1500 });
          } else {
            FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
          }
        }
      }

      init() {
        this._initSection();
      }

      /* ── Complementary: JSON + localStorage cache (never lose products) ── */
      _initComplementary() {
        const url = new URL(this.dataset.url, location.origin);
        const productId = url.searchParams.get('product_id');
        const limit = parseInt(url.searchParams.get('limit') || '10');
        const jsonUrl = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`;
        const cacheKey = 'comp-reco-' + productId;

        fetch(jsonUrl)
          .then(r => {
            if (!r.ok) throw new Error(r.status);
            return r.json();
          })
          .then(data => {
            const apiProducts = data.products || [];

            // Merge with cache: keep cached order, update data from API, never lose products
            let products;
            try {
              const cached = JSON.parse(localStorage.getItem(cacheKey));
              if (cached && cached.length) {
                const apiMap = new Map(apiProducts.map(p => [p.id, p]));
                // Use cached order as canonical — update product data from API if fresher
                const merged = cached.map(cp => apiMap.get(cp.id) || cp);
                // Append any new products from API not in cache
                for (const ap of apiProducts) {
                  if (!cached.some(cp => cp.id === ap.id)) merged.push(ap);
                }
                products = merged.slice(0, limit);
              } else {
                products = apiProducts;
              }
            } catch (e) {
              products = apiProducts;
            }

            if (products.length) {
              try { localStorage.setItem(cacheKey, JSON.stringify(products)); } catch (e) {}
              this._renderCards(products);
              try { localStorage.setItem('comp-html-' + productId, this.innerHTML); } catch (e) {}
              this.dispatchEvent(new CustomEvent('recommendations:loaded'));
            } else if (!this.innerHTML.trim()) {
              // Only remove block if nothing was rendered (no cache either)
              this.closest('.product__block')?.remove();
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
            }
          })
          .catch(() => {
            // API failed — if cache already rendered, do nothing
            if (this.innerHTML.trim()) return;
            // Last resort: section rendering
            this._initSection();
          });
      }

      /* ── Default: section rendering (related products, etc.) ── */
      _initSection() {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((responseText) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector('.shopify-section');

            if (sectionInnerHTML === null) return;

            const recommendations = sectionInnerHTML.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
            }

            if (recommendations.querySelector('.product-card, .pcard')) {
              this.dispatchEvent(new CustomEvent('recommendations:loaded'));
            } else {
              if (recommendations.classList.contains('complementary-products')) {
                this.closest('.product__block')?.remove();
              } else {
                this.closest('.shopify-section')?.remove();
              }
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /* ── Build complementary card HTML from JSON product data ── */
      _renderCards(products) {
        const heading = this.dataset.heading || '';
        const sectionId = this.dataset.sectionId || '';
        const fmt = (price) => FoxTheme.Currency.formatMoney(price, FoxTheme.settings.moneyFormat);
        const esc = (s) => {
          const d = document.createElement('div');
          d.textContent = s;
          return d.innerHTML;
        };

        let html = '<div class="complementary-cards">';
        if (heading) html += `<p class="complementary-cards__heading">${esc(heading)}</p>`;
        html += '<div class="complementary-cards__list">';

        for (const p of products) {
          const v = p.variants.find(v => v.available) || p.variants[0];
          const available = v && v.available;
          const inputId = `complementary-check-${sectionId}-${p.id}`;
          const productUrl = p.url || `/products/${p.handle}`;
          let imgSrc = '';
          if (typeof p.featured_image === 'string' && p.featured_image) {
            imgSrc = p.featured_image;
          } else if (p.images && p.images.length) {
            const firstImg = p.images[0];
            imgSrc = typeof firstImg === 'string' ? firstImg : (firstImg.src || '');
          }
          if (imgSrc && imgSrc.startsWith('//')) imgSrc = 'https:' + imgSrc;
          if (imgSrc) imgSrc = imgSrc.includes('?') ? imgSrc + '&width=128' : imgSrc + '?width=128';
          const price = v ? v.price : (p.price_min || '0');
          const comparePrice = v ? v.compare_at_price : null;
          const hasCompare = comparePrice && parseFloat(comparePrice) > parseFloat(price);

          html += `<div class="complementary-card product-card" data-product-id="${p.id}">`;

          if (available) {
            html += `<label class="complementary-card__checkbox" for="${inputId}">
              <input type="checkbox" id="${inputId}" class="complementary-card__input visually-hidden" data-variant-id="${v.id}" data-price="${price}">
              <span class="complementary-card__checkmark">
                <svg class="complementary-card__check-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </label>`;
          } else {
            html += `<span class="complementary-card__checkbox complementary-card__checkbox--disabled">
              <span class="complementary-card__checkmark complementary-card__checkmark--disabled"></span>
            </span>`;
          }

          html += `<a href="${productUrl}" class="complementary-card__image" aria-label="${esc(p.title)}">`;
          if (imgSrc) {
            html += `<img src="${imgSrc}" alt="" loading="lazy" width="46" height="46">`;
          } else {
            html += `<div style="width:46px;height:46px;background:rgba(0,0,0,0.06);border-radius:4px"></div>`;
          }
          html += `</a>
          <div class="complementary-card__content">
            <a href="${productUrl}" class="complementary-card__title">${esc(p.title)}</a>
            <div class="complementary-card__price-row">
              <span class="complementary-card__price">${fmt(price)}</span>`;
          if (hasCompare) {
            html += `<s class="complementary-card__compare-price">${fmt(comparePrice)}</s>`;
          }
          html += `</div></div></div>`;
        }

        html += '</div></div>';
        this.innerHTML = html;
      }
    }
  );
}
